import * as yup from "yup";
import {FormRateModel, RateEntityRequest, RateEntityResponse, RateSelectFE} from "../../../api/types/rate";
import dayjs from "dayjs";
import {GenericInterface, SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {
    fromSingleGenericBeToSingleGenericFe,
    fromSingleGenericFeToSingleGenericBe,
    i18nConvertNumberWithDecimals
} from "../../../utils";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import {maxNumberYupLimit, minNumberYupLimit} from "../../../constants";

export const initialRateBaseDefinitionValues: FormRateModel = {
    mainDefinition: {
        name: "",
        description: "",
        pickupFrom: "",
        pickupTo: "",
        reservationFrom: "",
        reservationTo: "",
        carGroup: "ACRISS",
        tpUnit: null,
        commercialCategory: null,
        commercialSegment: null,
        tax: false,
        dealer: null,
        legalEntity: null
    },
    otherDefinition: {
        currency: "EUR",
        unlimitedKm: false,
        includedKm: null,
        visibility: false,
        minimumDuration: null,
        maximumDuration: null,
        lateTolerance: null,
        lateDropOffRate: null,
        bestPrice: false
    },
    advancedBooking: {
        threshold: null,
        discountRate: null
    },
    referenceTariff: {
        discountIncreasePercentage: null,
        discountIncreaseFixed: null,
        enable: false,
        rate: null,
        discountIncreaseSelected: "discountIncreasePercentage"
    }
};

export const getRateBaseDefinitionValidationSchema = (t: Function, limits: GenericInterface, rate: RateEntityResponse | null) => {
    const yupReservationFrom = yupReservationFromFunc({
        t,
        id: rate?.id || null,
        reservationFrom: rate?.reservationFrom as string
    });

    const yupReservationTo = yupReservationToFunc({
        t,
        id: rate?.id || null,
        reservationTo: rate?.reservationTo as string,
        reservationFromFieldName: "reservationFrom"
    });

    const yupPickupFrom = yupPickupFromFunc({
        t,
        id: rate?.id || null,
        pickupFrom: rate?.pickupDateFrom as string
    });

    const yupPickupTo = yupPickupToFunc({
        t,
        id: rate?.id || null,
        reservationTo: rate?.reservationTo as string,
        pickupTo: rate?.pickupDateTo as string,
        pickupFrom: rate?.pickupDateFrom as string,
        pickupFromFieldName: "pickupFrom"
    });

    return yup.object().shape({
        mainDefinition: yup
            .object({
                name: yup
                    .string()
                    .required(t("translation:validation.fieldMandatory")),
                reservationFrom: yupReservationFrom,
                reservationTo: yupReservationTo,
                pickupFrom: yupPickupFrom,
                pickupTo: yupPickupTo,
                tax: yup.boolean().nullable(),
                carGroup: yup
                    .string()
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                tpUnit: yup.string()
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                dealer: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                legalEntity: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                commercialSegment: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                commercialCategory: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory"))
            }),
        otherDefinition: yup
            .object({
                unlimitedKm: yup
                    .boolean()
                    .nullable(),
                includedKm: yup.number()
                    .integer(t("pricing:alert.errors.integer"))
                    .typeError(t("pricing:alert.errors.integer"))
                    .nullable()
                    .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                    .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
                    // .when("unlimitedKm", {
                    //     is: false,
                    //     then: yup.number().required(t("translation:validation.fieldMandatory")).integer(t("pricing:alert.errors.integer")).typeError(t("pricing:alert.errors.integer")).nullable()
                    //         .positive(t("pricing:rentalPeriods.form.errors.positiveNumber")).max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit)
                    // }),
                currency: yup
                    .string()
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                // dealer: yup
                //     .object()
                //     .nullable()
                //     .required(t("translation:validation.fieldMandatory")),
                visibility: yup
                    .boolean()
                    .nullable(),
                lateTolerance: yup
                    .number()
                    .nullable()
                    .integer(t("pricing:alert.errors.integer"))
                    .typeError(t("pricing:alert.errors.integer"))
                    .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                    .min(limits.rateMinLateTolerance as number, t("pricing:validation.numberGreaterThan") + limits.rateMinLateTolerance)
                    .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
                minimumDuration: yup
                    .number()
                    .nullable()
                    .typeError(t("pricing:alert.errors.integer"))
                    .min(limits.rateDurationMin as number, t("pricing:validation.numberGreaterThan") + limits.rateDurationMin)
                    .max(yup.ref("maximumDuration"), t("pricing:validation.numberLowerThan") + t("pricing:rate.form.labels.otherDefinitionLabels.maximumDuration")),
                maximumDuration: yup
                    .number()
                    .nullable()
                    .typeError(t("pricing:alert.errors.integer"))
                    .max(limits.rateDurationMax as number, t("pricing:validation.numberLowerThan") + limits.rateDurationMax),
                lateDropOffRate: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable(),
                bestPrice: yup
                    .boolean()
                    .nullable()
            }),
        referenceTariff: yup
            .object({
                enable: yup
                    .boolean(),
                rate: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .when("enable", {
                        is: true,
                        then: yup
                            .object()
                            .shape({
                                code: yup.string(),
                                label: yup.string()
                            })
                            .nullable()
                            .required(t("translation:validation.fieldMandatory"))
                    }),
                discountIncreasePercentage: yup
                    .number()
                    .integer()
                    .nullable()
                    .typeError(t("pricing:alert.errors.integer"))
                    .min(-99, t("pricing:validation.numberGreaterThan") + -99)
                    .max(100, t("pricing:validation.numberLowerThan") + 100),
                discountIncreaseFixed: yup
                    .number()
                    .transform((o, v) => v ? parseFloat(v.replace(/,/g, ".")) : v)
                    .nullable()
                    .typeError(t("pricing:alert.errors.integer"))
                    .min(minNumberYupLimit, t("pricing:validation.numberGreaterThan") + minNumberYupLimit)
                    .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit)
            }),
        advancedBooking: yup
            .object({
                threshold: yup
                    .number()
                    .typeError(t("pricing:alert.errors.integer"))
                    .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                    .min(limits.rateAdvancedBookingThreshold as number, t("pricing:validation.numberGreaterThan") + limits.rateAdvancedBookingThreshold)
                    .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit)
                    .nullable(),
                discountRate: yup
                    .number()
                    .when("threshold", {
                        is: (threshold: number) => {
                            return threshold;
                        },
                        then: yup.number()
                            .nullable()
                            .typeError(t("pricing:alert.errors.integer"))
                            .required(t("translation:validation.fieldMandatory"))
                            .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                            .max(100, t("pricing:validation.numberLowerThan") + 100)
                    })
                    .nullable()
                    .typeError(t("pricing:alert.errors.integer"))
                    .positive(t("pricing:rentalPeriods.form.errors.positiveNumber"))
                    .max(100, t("pricing:validation.numberLowerThan") + 100),
            })
    });

};

export const buildEntityRequestFromFormValues = (formValues: FormRateModel, everyLegalEntitiesWithoutAll: string[], dealerFilteredOptions: string[]): RateEntityRequest => {
    return {
        commercialCategory: fromSingleGenericFeToSingleGenericBe(formValues.mainDefinition.commercialCategory as CommercialCategorySelectFE),
        name: formValues.mainDefinition.name,
        description: formValues.mainDefinition.description,
        reservationFrom: dayjs(formValues.mainDefinition.reservationFrom).toISOString(),
        reservationTo: dayjs(formValues.mainDefinition.reservationTo).toISOString(),
        pickupDateFrom: dayjs(formValues.mainDefinition.pickupFrom).toISOString(),
        pickupDateTo: dayjs(formValues.mainDefinition.pickupTo).toISOString(),
        tax: formValues.mainDefinition.tax,
        visibility: formValues.otherDefinition.visibility,
        durationMin: formValues.otherDefinition.minimumDuration as number,
        durationMax: formValues.otherDefinition.maximumDuration as number,
        lateTolerance: formValues.otherDefinition.lateTolerance as number,
        advancedBookingThreshold: formValues.advancedBooking.threshold as number,
        advancedBookingDiscount: formValues.advancedBooking.discountRate as number,
        relatedDiscountIncreasePercentage: formValues.referenceTariff.discountIncreasePercentage,
        relatedDiscountIncreaseFixed: formValues.referenceTariff.discountIncreaseFixed,
        dealerId: 1,
        // dealerId: formValues.otherDefinition.dealer?.code as number,
        referenceTariff: formValues.referenceTariff.rate ? formValues.referenceTariff.rate as RateSelectFE : null,
        linkedToReferenceTariff: formValues.referenceTariff.enable,
        tpUnit: formValues.mainDefinition.tpUnit as string,
        currency: formValues.otherDefinition.currency,
        bestPrice: formValues.otherDefinition.bestPrice,
        unlimitedKm: formValues.otherDefinition.unlimitedKm,
        includedKm: formValues.otherDefinition.includedKm as number,
        extraUnitRate: formValues.otherDefinition.lateDropOffRate ? formValues.otherDefinition.lateDropOffRate as RateSelectFE : null,
        carGroup: formValues.mainDefinition.carGroup,
        tpPaymentType: {id: 1, name: "Prepaid"},
        dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : formValues.mainDefinition.dealer?.code as string,
        legalEntity: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll[0] : formValues.mainDefinition.legalEntity?.code as string
    };
};

export const buildRateBaseDefinitionValues = (rate: RateEntityResponse, t: any): FormRateModel => {


    return {
        id: rate.id,
        mainDefinition: {
            name: rate.name,
            description: rate.description,
            pickupFrom: dayjs(rate.pickupDateFrom).toISOString(),
            pickupTo: dayjs(rate.pickupDateTo).toISOString(),
            reservationFrom: dayjs(rate.reservationFrom).toISOString(),
            reservationTo: dayjs(rate.reservationTo).toISOString(),
            carGroup: rate.carGroup,
            tpUnit: rate.tpUnit as string,
            commercialCategory: fromSingleGenericBeToSingleGenericFe(rate.commercialCategory) as CommercialCategorySelectFE,
            commercialSegment: fromSingleGenericBeToSingleGenericFe(rate.commercialCategory.commercialSegment) as SelectElementModelFE,
            tax: rate.tax,
            dealer: {code: rate.dealer, label: t('translation:dealers.'.concat(rate.dealer))},
            legalEntity: {code: rate.legalEntity, label: t('translation:legalEntities.'.concat(rate.legalEntity))}
        },
        otherDefinition: {
            currency: rate.currency,
            unlimitedKm: Boolean(rate.unlimitedKm),
            includedKm: rate.includedKm || null,
            visibility: Boolean(rate.visibility),
            minimumDuration: rate.durationMin,
            maximumDuration: rate.durationMax,
            lateTolerance: rate.lateTolerance,
            lateDropOffRate: rate.extraUnitRate ? {
                ...rate.extraUnitRate,
                code: (rate.extraUnitRate as SelectElementModelBE).id,
                label: (rate.extraUnitRate as SelectElementModelBE).id + " - " + (rate.extraUnitRate as SelectElementModelBE).name
            } : null,
            bestPrice: Boolean(rate.bestPrice)
        },
        advancedBooking: {
            threshold: rate.advancedBookingThreshold,
            discountRate: rate.advancedBookingDiscount
        },
        referenceTariff: {
            discountIncreasePercentage: rate.relatedDiscountIncreasePercentage || null,
            discountIncreaseFixed: rate.relatedDiscountIncreaseFixed ? i18nConvertNumberWithDecimals(Number(rate.relatedDiscountIncreaseFixed) / 100) : null,
            enable: Boolean(rate.linkedToReferenceTariff),
            rate: rate.referenceTariff ? {
                ...rate.referenceTariff,
                code: (rate.referenceTariff as SelectElementModelBE).id as number,
                label: (rate.referenceTariff as SelectElementModelBE).id + " - " + (rate.referenceTariff as SelectElementModelBE).name
            } : null,
            discountIncreaseSelected: rate.relatedDiscountIncreaseFixed ? "discountIncreaseFixed" : "discountIncreasePercentage"
        }
    };
};